import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

export default function PartnerSync() {
  const [tag, setTag] = useState("status::active");
  const [savedTag, setSavedTag] = useState("");
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, st, lg] = await Promise.all([
      api.get("/admin/settings"),
      api.get("/admin/partners/status"),
      api.get("/admin/partners/log"),
    ]);
    const t = s.data.partner_tag || "status::active";
    setTag(t);
    setSavedTag(t);
    setRunning(st.data.running);
    setLogs(lg.data.logs || []);
    return st.data.running;
  };

  useEffect(() => { load(); }, []);

  // poll while a sync runs
  useEffect(() => {
    if (!running) return;
    const t = setInterval(async () => {
      const still = await load();
      if (!still) toast.success("Partner sync finished");
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [running]);

  const saveTag = async () => {
    await api.patch("/admin/settings", { partner_tag: tag.trim() || "status::active" });
    setSavedTag(tag.trim() || "status::active");
    toast.success("Partner tag saved");
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/partners/sync");
      if (data.in_progress) toast.info("A sync is already running");
      else {
        toast.success("Partner sync started");
        setRunning(true);
      }
    } catch (e) {
      toast.error("Could not start the sync");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="partner-sync-page">
      <p className="btw-overline mb-1">Vendor directory</p>
      <h1 className="font-serif text-3xl mb-3">Partner sync</h1>
      <p className="text-sm text-[#5c5852] mb-6">
        GoHighLevel is the source of truth for current paid partners. Each sync reads the
        contacts carrying the partner tag in each sub-account (read-only), stars every
        matching vendor here, and unstars vendors no longer tagged — unless their star is
        pinned on the review page. Nothing is ever written back to GHL by this sync.
      </p>

      <div className="bg-white border border-[#dcd3c6] p-4 mb-6">
        <label className="text-xs uppercase tracking-wider text-[#5c5852] block mb-1">Partner tag</label>
        <div className="flex gap-2 items-center flex-wrap">
          <input value={tag} onChange={(e) => setTag(e.target.value)} data-testid="partner-tag-input"
            className="px-3 py-2 border border-[#dcd3c6] bg-white text-sm w-64" />
          {tag.trim() !== savedTag && (
            <button className="btw-btn text-xs px-3 py-2" data-testid="partner-tag-save" onClick={saveTag}>Save tag</button>
          )}
          <button onClick={run} disabled={busy || running} data-testid="run-partner-sync" className="btw-btn text-xs px-4 py-2 inline-flex items-center gap-1">
            <RefreshCw size={13} className={running ? "animate-spin" : ""} />
            {running ? "Syncing…" : "Sync partners now"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#dcd3c6]" data-testid="sync-log">
        <p className="text-xs uppercase tracking-wider text-[#5c5852] px-4 pt-3">Sync log</p>
        {logs.length === 0 && <p className="text-sm text-[#5c5852] px-4 py-4">No syncs yet — one runs daily at 06:30 UTC, or use the button above.</p>}
        {logs.map((l) => (
          <details key={l.id} className="border-t border-[#e2d3c0] px-4 py-2" data-testid={`sync-log-${l.id}`}>
            <summary className="text-sm cursor-pointer">
              {new Date(l.at).toLocaleString()} — tag “{l.tag}” ·{" "}
              {l.status === "ok" ? "ok" : l.status}
              {l.error && <span className="text-[#b35c5c]"> · {l.error}</span>}
            </summary>
            <div className="mt-2 grid gap-3 pb-2">
              {["dwh", "dwt"].map((p) => l.pubs?.[p] && (
                <div key={p} className="text-xs">
                  <p className="font-semibold uppercase">{p} — {l.pubs[p].status}{typeof l.pubs[p].tagged_contacts === "number" ? ` · ${l.pubs[p].tagged_contacts} tagged contacts` : ""}</p>
                  {l.pubs[p].error && <p className="text-[#b35c5c]">{l.pubs[p].error}</p>}
                  {l.pubs[p].starred?.length > 0 && <p>Starred: {l.pubs[p].starred.join(", ")}</p>}
                  {l.pubs[p].unstarred?.length > 0 && <p>Unstarred: {l.pubs[p].unstarred.join(", ")}</p>}
                  {l.pubs[p].unmatched?.length > 0 && (
                    <p>No matching vendor: {l.pubs[p].unmatched.map((u) => u.name || u.email || u.website).join(", ")}</p>
                  )}
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
